import React, { useEffect, useRef, useState } from "react";
import "./style.css";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5stock from "@amcharts/amcharts5/stock";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { calculate_theme_color } from "../../../services/util/newsSentiment";
import { useSelector } from "react-redux";


interface amcharts {
  data: any,
  selectedStock: any,
  selectedDate: any

}

const Amcharts: React.FC<amcharts> = ({ data, selectedStock, selectedDate }) => {
  const chartDiv = useRef(null); // Reference to the div where the chart will be rendered
  const secondData: any = useSelector((state: any) => { return state.liveSentimentArr.chartsArray })
  const date: any = (new Date()).toLocaleDateString("en-CA")
  const theme: any = useSelector((state: any) => {
    return state.theme.isTheme;
  })

  const secondDataRef = useRef<any>(secondData);

  // Keep the ref updated with the latest value of secondData
  useEffect(() => {
    secondDataRef.current = secondData;
  }, [secondData]);

  useEffect(() => {
    // Initialize the chart after the component mounts
    let root: any = am5.Root.new(chartDiv.current!);
    root._logo.dispose();


    root.setThemes([
      am5themes_Animated.new(root)
    ]);

    // Create a stock chart
    let stockChart = root.container.children.push(
      am5stock.StockChart.new(root, {
        paddingRight: 0
      })
    );

    // Set number formatting
    root.numberFormatter.set("numberFormat", "#,###.00");

    // Create a main stock panel (chart)
    let mainPanel = stockChart.panels.push(
      am5stock.StockPanel.new(root, {
        wheelY: "zoomX",
        panX: true,
        panY: true
      })
    );

    // Create value axis
    // Create axes
    const valueAxis: any = mainPanel.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          pan: "zoom",
          stroke: am5.color(`${calculate_theme_color(theme)}`),
          strokeOpacity: 1
        }),
        extraMin: 0.1,
        tooltip: am5.Tooltip.new(root, {}),
        numberFormat: "#,###.00",
        extraTooltipPrecision: 2,
      })
    );

    valueAxis.get("renderer").labels.template.setAll(
      {
        fill: am5.color(`${calculate_theme_color(theme)}`) // Red color for X-axis labels 
      });



    const dateAxis: any = mainPanel.xAxes.push(
      am5xy.GaplessDateAxis.new(root, {
        extraMax: 0.05,
        baseInterval: {
          timeUnit: "minute",
          count: 1,
        },
        groupData: true,
        renderer: am5xy.AxisRendererX.new(root, {
          stroke: am5.color(`${calculate_theme_color(theme)}`),
          strokeOpacity: 1,
          minorGridEnabled: false,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    dateAxis.get("renderer").labels.template.setAll(
      {
        fill: am5.color(`${calculate_theme_color(theme)}`) // Red color for X-axis labels 
      });



    // add range which will show current value

    let currentValueDataItem = valueAxis.createAxisRange(valueAxis.makeDataItem({ value: 0 }));
    let currentLabel = currentValueDataItem.get("label");
    if (currentLabel) {
      currentLabel.setAll({
        fill: am5.color(0xffffff),
        background: am5.Rectangle.new(root, { fill: am5.color(0x000000) })
      })
    }

    let currentGrid = currentValueDataItem.get("grid");
    if (currentGrid) {
      currentGrid.setAll({ strokeOpacity: 0.5, strokeDasharray: [2, 5] });
    }






    // Add series (Candlestick series)
    let valueSeries = mainPanel.series.push(
      am5xy.CandlestickSeries.new(root, {
        name: selectedStock,
        clustered: false,
        valueXField: "date",
        valueYField: "close",
        highValueYField: "high",
        lowValueYField: "low",
        openValueYField: "open",
        calculateAggregates: true,
        xAxis: dateAxis,
        yAxis: valueAxis,
        legendValueText:
          "open: [bold]{openValueY}[/] high: [bold]{highValueY}[/] low: [bold]{lowValueY}[/] close: [bold]{valueY}[/]",
        legendRangeValueText: "",
        stroke: am5.color("rgb(0, 204, 102)")
      })
    );
    valueSeries.columns.template.states.create("riseFromOpen", {
      fill: am5.color('#ff0000'),
      stroke: am5.color('#ff0000')
    });

    valueSeries.columns.template.states.create("dropFromOpen", {
      fill: am5.color("rgb(0, 204, 102)"),
      stroke: am5.color("rgb(0, 204, 102)")
    });


    stockChart.set("stockSeries", valueSeries);

    valueSeries.columns.template.adapters.add("fill", (fill: any, target: any) => {
      return target.dataItem?.get("valueY") > target.dataItem?.get("openValueY")
        ? am5.color("rgb(0, 179, 89)") // Green for up candles 
        : am5.color(0xff0000); // Red for down candles
    });

    valueSeries.columns.template.adapters.add("stroke", (stroke: any, target: any) => {
      return target.dataItem?.get("valueY") > target.dataItem?.get("openValueY")
        ? am5.color("rgb(0, 179, 89)") // Green for up candles 
        : am5.color(0xff0000); // Red for down candles 
    });



    const valueLegend: any = mainPanel.plotContainer.children.push(
      am5stock.StockLegend.new(root, {
        stockChart: stockChart,
      })
    );


    valueLegend.data.setAll([valueSeries]);

    mainPanel.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        yAxis: valueAxis,
        xAxis: dateAxis,
        snapToSeries: [valueSeries],
        snapToSeriesBy: "y!"
      })
    );

    // Scrollbar setup
    let scrollbar = mainPanel.set(
      "scrollbarX",
      am5xy.XYChartScrollbar.new(root, {
        orientation: "horizontal",
        height: 50
      })
    );
    stockChart.toolsContainer.children.push(scrollbar);


    const sbDateAxis: any = scrollbar.chart.xAxes.push(
      am5xy.GaplessDateAxis.new(root, {
        baseInterval: {
          timeUnit: "day",
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minorGridEnabled: true
        }),
      })
    );

    const sbValueAxis: any = scrollbar.chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    );

    let sbSeries = scrollbar.chart.series.push(
      am5xy.LineSeries.new(root, {
        valueYField: "close",
        valueXField: "date",
        xAxis: sbDateAxis,
        yAxis: sbValueAxis
      })
    );

    sbSeries.fills.template.setAll({
      visible: true,
      fillOpacity: 0.3
    });

    // Function to add custom bullets
    function addBullet(date: any, signalValue: any) {
      valueSeries.events.once("datavalidated", function () {
        let axisPosition = dateAxis.dateToPosition(new Date(date));
        let seriesDataItem = dateAxis.getSeriesItem(valueSeries, axisPosition, 0);
        if (seriesDataItem) {
          const bullet = am5.Container.new(root, {});
          const isBuySignal = signalValue === 1;
          const gap = 5;


          bullet.children.push(
            am5.Triangle.new(root, {
              width: 10,
              height: 10,
              fill: am5.color(isBuySignal ? "rgb(74, 191, 0)" : "rgb(255, 0, 0)"),
              stroke: am5.color(isBuySignal ? "rgb(74, 191, 0)" : "rgb(255, 0, 0)"),
              centerY: isBuySignal ? am5.p100 : am5.p0,
              centerX: am5.p50,
              rotation: isBuySignal ? 0 : 180,
              y: isBuySignal ? 30 + gap : -30 - gap,
            })
          );

          valueSeries.addBullet(seriesDataItem, am5.Bullet.new(root, { sprite: bullet }));
        }
      });
    }

    // Process data and add bullets for signals
    data.forEach((item: any) => {
      if (item.value) {
        addBullet(item.date, item.value);
      }
    });

    // Add cursor
    mainPanel.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        yAxis: valueAxis,
        xAxis: dateAxis,
        snapToSeries: [valueSeries],
      })
    );

    const processedData = data.map((item: any) => ({
      date: new Date(item.date).getTime(), // Convert date string to timestamp
      open: item.open,
      high: item.high,
      low: item.low,
      close: item.close,
    }));

    // Set data to series
    valueSeries.data.setAll(processedData);
    sbSeries.data.setAll(processedData);


    const watermark = root.container.children.push(
      am5.Label.new(root, {
        text: selectedStock,
        fontSize: 50,
        fontStyle: "italic",
        fontWeight: "bold",
        opacity: 0.15,
        rotation: 0,
        centerX: am5.percent(50),
        centerY: am5.percent(50),
        x: am5.percent(50),
        y: am5.percent(50),
      }));



    let currentMinute: any = null; // Track the current minute being processed

    const interval = setInterval(() => {
      if (date == selectedDate) {
        const now = new Date();
        const date: any = now.toLocaleDateString("en-CA")
        console.log("chaeck here:", date, selectedDate, currentLabel && date == selectedDate)
        // Define start and end times
        const time = now.toTimeString().slice(0, 6) + "00"; // Current time as hh:mm:00
        const startTime = "09:15:00";
        const endTime = "15:30:00";

        // Check if the current time is within the allowed range
        if ((time < startTime || time > endTime)) {
          // Outside trading hours; skip updates
          return;
        }
        const currentSecondData = secondDataRef.current;

        let lastDataObject = valueSeries.data.getIndex(valueSeries.data.length - 1);

        if (lastDataObject && currentSecondData[time]) {
          const date = now.getTime(); // Current timestamp
          const minute = now.getMinutes(); // Current minute value
          const latestClose = currentSecondData[time][3];

          let high = lastDataObject.high;
          let low = lastDataObject.low;
          let open = lastDataObject.open;

          if (currentMinute === null) {
            currentMinute = minute; // Initialize the currentMinute tracker
          }

          // If a new minute starts, add a new data point
          if (minute !== currentMinute) {

            // Push the completed candle for the previous minute
            const newCandle = {
              date: date, // Timestamp for the new candle
              open: currentSecondData[time][0],
              high: currentSecondData[time][1],
              low: currentSecondData[time][2],
              close: currentSecondData[time][3],
            };

            valueSeries.data.push(newCandle);
            currentMinute = minute; // Update the tracker to the new minute
          } else {

            // Update the current minute's candle
            if (currentSecondData[time][1] > high) high = currentSecondData[time][1];
            if (currentSecondData[time][2] < low) low = currentSecondData[time][2];

            const updatedData = {
              date: lastDataObject.date, // Keep the same date as the last candle
              open: open,
              high: high,
              low: low,
              close: currentSecondData[time][3], // Update close price
            };

            valueSeries.data.setIndex(valueSeries.data.length - 1, updatedData);

            // updatePriceIndicator(latestClose);
            if (currentLabel) {
              currentValueDataItem.animate({ key: "value", to: latestClose, duration: 500, easing: am5.ease.out(am5.ease.cubic) });
              currentLabel.set("text", stockChart.getNumberFormatter().format(latestClose));
              let bg = currentLabel.get("background");
              if (bg) {
                if (latestClose < open) {
                  bg.set("fill", root.interfaceColors.get("negative"));
                }
                else {
                  bg.set("fill", root.interfaceColors.get("positive"));
                }
              }
            }

          }
        }
      }
    }, 1000);



    // Cleanup on component unmount
    return () => {
      clearInterval(interval);
      root.dispose();
    };
  }, [data, theme, selectedDate]);

  return (
    <div
      id="chartdiv"
      ref={chartDiv}
      style={{ width: "100%", height: "500px" }}
    ></div>
  );
};

export default Amcharts;
